import { API_BASE } from "../config/api";

/** Build full URL for backend API paths. */
function buildUrl(urlOrPath) {
  if (urlOrPath.startsWith("http")) return urlOrPath;
  const base = (API_BASE || "").replace(/\/$/, "");
  const path = urlOrPath.startsWith("/") ? urlOrPath : `/${urlOrPath}`;
  return `${base}${path}`;
}

/** Request deduplication: in-flight GET requests for same URL share the same Promise. */
const inFlight = new Map();

/**
 * API request wrapper. Automatically adds Idempotency-Key for write operations.
 * GET requests are deduplicated: same URL returns the same in-flight Promise.
 */
export async function apiRequest(urlOrPath, options = {}) {
  const url = buildUrl(urlOrPath);
  const method = (options.method || "GET").toUpperCase();
  const dedupKey = method === "GET" ? url : null;
  if (dedupKey) {
    const existing = inFlight.get(dedupKey);
    if (existing) return existing;
  }

  const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  // Fallback: send token from localStorage as Bearer if cookie may not be available (mobile browsers)
  if (!headers["Authorization"]) {
    try {
      const storedToken = typeof window !== "undefined" && window.localStorage ? localStorage.getItem("token") : null;
      if (storedToken) {
        headers["Authorization"] = "Bearer " + storedToken;
      }
    } catch(e) {}
  }

  // Auth via httpOnly cookies — credentials: "include" sends them automatically

  // Automatically attach Idempotency-Key for write operations
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    if (!headers["Idempotency-Key"]) {
      headers["Idempotency-Key"] =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : Date.now().toString() + Math.random().toString(36);
    }
  }

  const promise = fetch(url, {
    ...options,
    method,
    headers,
    credentials: "include",
  }).then(async (response) => {
    if (dedupKey) inFlight.delete(dedupKey);
    const text = await response.text();

    if (!response.ok) {
      const err = new Error(text || "API request failed");
      err.status = response.status;
      err.data = null;
      try {
        err.data = text ? JSON.parse(text) : null;
      } catch {}
      throw err;
    }

    if (response.status === 204) return null;

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }).catch((err) => {
    if (dedupKey) inFlight.delete(dedupKey);
    throw err;
  });

  if (dedupKey) inFlight.set(dedupKey, promise);
  return promise;
}
